// UPDATED VERSION - 2025-11-20 - WITH ALL 9 SECTIONS INCLUDING VIDEO
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import DataTable from '../../components/admin/DataTable';
import ImageUpload from '../../components/admin/ImageUpload';
import FileUpload from '../../components/admin/FileUpload';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { getFileUrl } from '../../lib/utils';

const ProgramsManagement = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title_ar: '', title_en: '', 
    description_ar: '', description_en: '',
    about_ar: '', about_en: '',
    vision_ar: '', vision_en: '',
    mission_ar: '', mission_en: '',
    objectives_ar: '', objectives_en: '',
    career_fields_ar: '', career_fields_en: '',
    study_plan_ar: '', study_plan_en: '',
    study_plan_pdf: '',
    learning_outcomes_file: '',
    course_schedules: [],
    requirements_ar: '', requirements_en: '',
    admission_requirements_file: '',
    duration: '', degree: 'بكالوريوس',
    image: '', video_url: '', is_published: true, order: 0
  });
  const [newSchedule, setNewSchedule] = useState({ name_ar: '', name_en: '', level: '', file_url: '' });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { fetchPrograms(); }, []);

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/programs/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      setPrograms(await response.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${BACKEND_URL}/api/programs/${editing.id}` : `${BACKEND_URL}/api/programs/`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        fetchPrograms();
        resetForm();
        alert('تم الحفظ بنجاح');
      }
    } catch (error) {
      alert('حدث خطأ');
    }
  };

  const resetForm = () => {
    setFormData({
      title_ar: '', title_en: '', 
      description_ar: '', description_en: '',
      about_ar: '', about_en: '',
      vision_ar: '', vision_en: '',
      mission_ar: '', mission_en: '',
      objectives_ar: '', objectives_en: '',
      career_fields_ar: '', career_fields_en: '',
      study_plan_ar: '', study_plan_en: '',
      study_plan_pdf: '',
      learning_outcomes_file: '',
      course_schedules: [],
      requirements_ar: '', requirements_en: '',
      admission_requirements_file: '',
      duration: '', degree: 'بكالوريوس',
      image: '', video_url: '', is_published: true, order: 0
    });
    setNewSchedule({ name_ar: '', name_en: '', level: '', file_url: '' });
    setEditing(null);
    setShowForm(false);
  };

  const columns = [
    { header: 'العنوان', accessor: 'title_ar' },
    { header: 'المدة', accessor: 'duration' },
    { header: 'الدرجة', accessor: 'degree' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E]">إدارة البرامج الأكاديمية</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#0F388E]">
          <Plus className="ml-2" />إضافة برنامج
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editing ? 'تعديل' : 'إضافة'} برنامج أكاديمي</CardTitle>
            <p className="text-sm text-gray-500 mt-1">املأ جميع الحقول المطلوبة لإضافة برنامج جديد</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* المعلومات الأساسية */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg space-y-4 border border-blue-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                  المعلومات الأساسية
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    placeholder="عنوان البرنامج بالعربي *" 
                    value={formData.title_ar}
                    onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]" 
                    required 
                  />
                  <input 
                    type="text" 
                    placeholder="Program Title in English" 
                    value={formData.title_en}
                    onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]" 
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    placeholder="المدة (مثال: 4 سنوات) *" 
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]" 
                    required 
                  />
                  <input 
                    type="text" 
                    placeholder="الدرجة (مثال: بكالوريوس) *" 
                    value={formData.degree}
                    onChange={(e) => setFormData({...formData, degree: e.target.value})}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]" 
                    required 
                  />
                </div>
              </div>

              {/* الوصف */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg space-y-4 border border-green-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  الوصف القصير
                </h3>
                <RichTextEditor 
                  label="الوصف بالعربي *" 
                  value={formData.description_ar}
                  onChange={(v) => setFormData({...formData, description_ar: v})} 
                  rows={3} 
                />
                <RichTextEditor 
                  label="Description in English" 
                  value={formData.description_en}
                  onChange={(v) => setFormData({...formData, description_en: v})} 
                  rows={3} 
                />
              </div>

              {/* نبذة عن البرنامج */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg space-y-4 border border-purple-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  نبذة عن البرنامج
                </h3>
                <RichTextEditor 
                  label="نبذة عن البرنامج بالعربي" 
                  value={formData.about_ar}
                  onChange={(v) => setFormData({...formData, about_ar: v})} 
                  rows={5} 
                />
                <RichTextEditor 
                  label="About the Program in English" 
                  value={formData.about_en}
                  onChange={(v) => setFormData({...formData, about_en: v})} 
                  rows={5} 
                />
              </div>

              {/* الرؤية والرسالة والأهداف */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg space-y-4 border border-amber-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">4</span>
                  الرؤية والرسالة والأهداف
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RichTextEditor 
                    label="الرؤية بالعربي" 
                    value={formData.vision_ar}
                    onChange={(v) => setFormData({...formData, vision_ar: v})} 
                    rows={4} 
                  />
                  <RichTextEditor 
                    label="Vision in English" 
                    value={formData.vision_en}
                    onChange={(v) => setFormData({...formData, vision_en: v})} 
                    rows={4} 
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RichTextEditor 
                    label="الرسالة بالعربي" 
                    value={formData.mission_ar}
                    onChange={(v) => setFormData({...formData, mission_ar: v})} 
                    rows={4} 
                  />
                  <RichTextEditor 
                    label="Mission in English" 
                    value={formData.mission_en}
                    onChange={(v) => setFormData({...formData, mission_en: v})} 
                    rows={4} 
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <RichTextEditor 
                    label="الأهداف بالعربي" 
                    value={formData.objectives_ar}
                    onChange={(v) => setFormData({...formData, objectives_ar: v})} 
                    rows={4} 
                  />
                  <RichTextEditor 
                    label="Objectives in English" 
                    value={formData.objectives_en}
                    onChange={(v) => setFormData({...formData, objectives_en: v})} 
                    rows={4} 
                  />
                </div>
              </div>

              {/* مجالات العمل */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg space-y-4 border border-cyan-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">5</span>
                  مجالات العمل بعد التخرج
                </h3>
                <RichTextEditor 
                  label="مجالات العمل بالعربي" 
                  value={formData.career_fields_ar}
                  onChange={(v) => setFormData({...formData, career_fields_ar: v})} 
                  rows={5} 
                />
                <RichTextEditor 
                  label="Career Fields in English" 
                  value={formData.career_fields_en}
                  onChange={(v) => setFormData({...formData, career_fields_en: v})} 
                  rows={5} 
                />
              </div>

              {/* الخطة الدراسية */}
              <div className="bg-gradient-to-r from-rose-50 to-red-50 p-6 rounded-lg space-y-4 border border-rose-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">6</span>
                  الخطة الدراسية
                </h3>
                <RichTextEditor 
                  label="الخطة الدراسية بالعربي (نصية)" 
                  value={formData.study_plan_ar}
                  onChange={(v) => setFormData({...formData, study_plan_ar: v})} 
                  rows={5} 
                />
                <RichTextEditor 
                  label="Study Plan in English (Text)" 
                  value={formData.study_plan_en}
                  onChange={(v) => setFormData({...formData, study_plan_en: v})} 
                  rows={5} 
                />
                <FileUpload 
                  label="ملف الخطة الدراسية (PDF)" 
                  value={formData.study_plan_pdf}
                  onChange={(v) => setFormData({...formData, study_plan_pdf: v})} 
                  accept=".pdf"
                />
                <FileUpload 
                  label="ملف مخرجات التعلم (PDF) / Learning Outcomes File" 
                  value={formData.learning_outcomes_file}
                  onChange={(v) => setFormData({...formData, learning_outcomes_file: v})} 
                  accept=".pdf"
                />
              </div>

              {/* الجداول الدراسية */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg space-y-4 border border-purple-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">6.5</span>
                  الجداول الدراسية
                </h3>
                
                {/* Form to add new schedule */}
                <div className="bg-white p-4 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-gray-700 mb-3">إضافة جدول دراسي جديد</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="اسم الجدول (مثلاً: الفصل الأول - السنة الأولى)"
                      value={newSchedule.name_ar}
                      onChange={(e) => setNewSchedule({...newSchedule, name_ar: e.target.value})}
                      className="px-4 py-2 border rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Schedule Name (Optional)"
                      value={newSchedule.name_en}
                      onChange={(e) => setNewSchedule({...newSchedule, name_en: e.target.value})}
                      className="px-4 py-2 border rounded-lg"
                    />
                    <div>
                      <input
                        type="text"
                        placeholder="المستوى (مثلاً: المستوى الأول)"
                        value={newSchedule.level}
                        onChange={(e) => setNewSchedule({...newSchedule, level: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                        list="levels-list"
                      />
                      <datalist id="levels-list">
                        <option value="المستوى الأول" />
                        <option value="المستوى الثاني" />
                        <option value="المستوى الثالث" />
                        <option value="المستوى الرابع" />
                        <option value="المستوى الخامس" />
                      </datalist>
                    </div>
                  </div>
                  <div className="mt-3">
                    <FileUpload
                      label="ملف الجدول الدراسي (PDF أو صورة)"
                      value={newSchedule.file_url}
                      onChange={(v) => setNewSchedule({...newSchedule, file_url: v})}
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      if (newSchedule.name_ar && newSchedule.level && newSchedule.file_url) {
                        setFormData({
                          ...formData,
                          course_schedules: [
                            ...(formData.course_schedules || []),
                            { ...newSchedule, id: Date.now().toString() }
                          ]
                        });
                        setNewSchedule({ name_ar: '', name_en: '', level: '', file_url: '' });
                      } else {
                        alert('يرجى ملء جميع الحقول المطلوبة');
                      }
                    }}
                    className="mt-3 bg-purple-600 hover:bg-purple-700"
                  >
                    إضافة الجدول
                  </Button>
                </div>

                {/* List of schedules */}
                {formData.course_schedules && formData.course_schedules.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-700">الجداول المضافة:</h4>
                    {formData.course_schedules.map((schedule, index) => (
                      <div key={schedule.id || index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-purple-200">
                        <div className="flex-1">
                          <p className="font-semibold text-[#0F388E]">{schedule.name_ar}</p>
                          <p className="text-sm text-gray-600">المستوى: {schedule.level}</p>
                          {schedule.file_url && (
                            <a 
                              href={getFileUrl(schedule.file_url)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              عرض الملف
                            </a>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              course_schedules: formData.course_schedules.filter((_, i) => i !== index)
                            });
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* شروط القبول */}
              <div className="bg-gradient-to-r from-teal-50 to-green-50 p-6 rounded-lg space-y-4 border border-teal-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">7</span>
                  شروط القبول
                </h3>
                <RichTextEditor 
                  label="شروط القبول بالعربي" 
                  value={formData.requirements_ar}
                  onChange={(v) => setFormData({...formData, requirements_ar: v})} 
                  rows={4} 
                />
                <RichTextEditor 
                  label="Requirements in English" 
                  value={formData.requirements_en}
                  onChange={(v) => setFormData({...formData, requirements_en: v})} 
                  rows={4} 
                />
                <FileUpload 
                  label="ملف شروط القبول (PDF / وثيقة)" 
                  value={formData.admission_requirements_file} 
                  onChange={(v) => setFormData({...formData, admission_requirements_file: v})} 
                  accept=".pdf,.doc,.docx" 
                />
              </div>

              {/* صورة البرنامج */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg space-y-4 border border-indigo-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">8</span>
                  صورة البرنامج
                </h3>
                <ImageUpload 
                  label="صورة البرنامج" 
                  value={formData.image}
                  onChange={(v) => setFormData({...formData, image: v})} 
                />
              </div>

              {/* فيديو عن البرنامج */}
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-lg space-y-4 border border-red-100">
                <h3 className="font-bold text-[#0F388E] text-lg flex items-center gap-2">
                  <span className="bg-[#0F388E] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">9</span>
                  فيديو عن البرنامج (YouTube)
                </h3>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    رابط فيديو YouTube
                  </label>
                  <input
                    type="url"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={formData.video_url}
                    onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500">
                    💡 أدخل رابط الفيديو من YouTube (مثال: https://www.youtube.com/watch?v=dQw4w9WgXcQ)
                  </p>
                  {formData.video_url && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">✅ الرابط صحيح - سيتم عرض الفيديو في صفحة تفاصيل البرنامج</p>
                    </div>
                  )}
                </div>
              </div>

              {/* أزرار الحفظ */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm} className="px-6">
                  إلغاء
                </Button>
                <Button type="submit" className="bg-[#0F388E] px-6">
                  {editing ? 'تحديث البرنامج' : 'حفظ البرنامج'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable columns={columns} data={programs}
            onEdit={(item) => {
              setEditing(item);
              setFormData({
                title_ar: item.title_ar || '',
                title_en: item.title_en || '',
                description_ar: item.description_ar || '',
                description_en: item.description_en || '',
                about_ar: item.about_ar || '',
                about_en: item.about_en || '',
                vision_ar: item.vision_ar || '',
                vision_en: item.vision_en || '',
                mission_ar: item.mission_ar || '',
                mission_en: item.mission_en || '',
                objectives_ar: item.objectives_ar || '',
                objectives_en: item.objectives_en || '',
                career_fields_ar: item.career_fields_ar || '',
                career_fields_en: item.career_fields_en || '',
                study_plan_ar: item.study_plan_ar || '',
                study_plan_en: item.study_plan_en || '',
                study_plan_pdf: item.study_plan_pdf || '',
                learning_outcomes_file: item.learning_outcomes_file || '',
                course_schedules: Array.isArray(item.course_schedules) ? item.course_schedules : [],
                requirements_ar: item.requirements_ar || '',
                requirements_en: item.requirements_en || '',
                admission_requirements_file: item.admission_requirements_file || item.admission_file || '',
                duration: item.duration || '',
                degree: item.degree || 'بكالوريوس',
                image: item.image || '',
                video_url: item.video_url || '',
                is_published: item.is_published !== undefined ? item.is_published : true,
                order: item.order || 0
              });
              setShowForm(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onDelete={async (item) => {
              if (window.confirm('هل أنت متأكد من رغبتك في حذف هذا البرنامج؟')) {
                await fetch(`${BACKEND_URL}/api/programs/${item.id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                });
                fetchPrograms();
              }
            }}
            loading={loading} />
        </CardContent>
      </Card>
    </div>
  );
};

export default ProgramsManagement;
